<script>
  import { fade, slide } from "svelte/transition";
  import { link } from "svelte-routing";
  import pb from '../../lib/pocketbase';
  import NotificationDropdown from "components/Dropdowns/NotificationDropdown.svelte";
  import UserDropdown from "components/Dropdowns/UserDropdown.svelte";
  import { onMount, onDestroy } from "svelte";
  import { authStore, initializeAuth } from '../../lib/authStore.js';
  import { get } from 'svelte/store';
  import { isMobileStore } from '../../stores/media';
  import { t, locale } from 'svelte-i18n';

  export let location;

  let collapseShow = "hidden";
  let userData = null;
  let error = null;
  let objetoItems = []; // List items from PocketBase
  let hierarchicalItems = [];
  let searchTerm = ''; // Variable to hold the search term
  let expandedChild = null;
  let isFetching = false; // Flag to indicate if a fetch is in progress

  function toggleCollapseShow(classes) {
    collapseShow = classes;
  }

  // Function to get the correct language from descrip JSON
  function getDescripText(descrip) {
    if (!descrip) return '';
    
    try {
      // If descrip is already a string, return it (backward compatibility)
      if (typeof descrip === 'string') {
        return descrip;
      }
      
      // If descrip is a JSON object, parse it and get the correct language
      const descripObj = typeof descrip === 'object' ? descrip : JSON.parse(descrip);
      const currentLocale = $locale || 'en';
      
      // Return the appropriate language based on current locale
      let result;
      if (currentLocale === 'es') {
        result = descripObj.esp || descripObj.eng || descrip;
      } else {
        result = descripObj.eng || descripObj.esp || descrip;
      }
      
      // Ensure we always return a string
      return String(result || '');
    } catch (error) {
      console.warn('Error parsing descrip JSON:', error);
      return String(descrip || ''); // Fallback to original value as string
    }
  }

  // Toggle visibility of child items
  function toggleChild(childId) {
    expandedChild = expandedChild === childId ? null : childId;
  }

  // Organize items hierarchically
  function buildHierarchy(items) {
    const lookup = {};
    items.forEach(item => {
      lookup[item._id] = { ...item, children: [] };
    });

    const hierarchy = [];
    items.forEach(item => {
      if (item.parent_id) {
        lookup[item.parent_id]?.children.push(lookup[item._id]);
      } else {
        hierarchy.push(lookup[item._id]);
      }
    });
    return hierarchy;
  }

  function updateHierarchy() {
    hierarchicalItems = buildHierarchy(objetoItems);
  }

  async function fetchAndUpdateObjetoItems() {
    if (isFetching) return; // Prevent concurrent fetch calls
    isFetching = true;

    try {
      const { authData } = get(authStore);
      if (authData) {
        let userRole = JSON.parse(authData).role;

        // Obtener todos los objetos de la colección 'objeto'
        objetoItems = await pb.collection('objeto').getFullList();

        // Obtener roles de objetos para el usuario autenticado
        const objetoRoles = await pb.collection('role_objeto').getFullList({
          filter: `role = "${userRole}"` // Filtrar por el rol del usuario
        });

        const allowedObjetoIds = objetoRoles.map(item => item.objeto); // Obtener IDs de objetos permitidos

        // Filtrar objetoItems según los IDs permitidos
        objetoItems = objetoItems.filter(item => allowedObjetoIds.includes(item.id));

        updateHierarchy();
      }
    } catch (error) {
      console.error('Error fetching objeto items:', error);
    } finally {
      isFetching = false; // Reset the flag when done
    }
  }

  async function subscribeToRealtime() {
    await pb.collection('role_objeto').subscribe('*', async (e) => {
      console.log('Realtime event:', e);
      // Throttle the fetch call to prevent rapid firing
      if (!isFetching) {
        await fetchAndUpdateObjetoItems();
      }
    });
  }

  onMount(async () => {
    await initializeAuth();
    await fetchAndUpdateObjetoItems(); // Call fetch on mount
    subscribeToRealtime(); // Start subscribing to real-time updates
  });

  onDestroy(() => {
    pb.collection('role_objeto').unsubscribe('*');
  });

  // Filtering function
  function filterItems(items, search) {
    const lowerSearch = search.toLowerCase();
    return items.reduce((acc, item) => {
      const filteredChildren = filterItems(item.children, search);
      const itemMatches = getDescripText(item.descrip).toLowerCase().includes(lowerSearch);
      if (itemMatches || filteredChildren.length > 0) {
        acc.push({ ...item, children: filteredChildren });
      }
      return acc;
    }, []);
  }

  // Reactive statement for filtered items
  $: filteredItems = filterItems(hierarchicalItems, searchTerm);
</script>


<nav
  class="md:left-0 md:block md:fixed md:top-0 md:bottom-0 md:overflow-y-auto md:flex-row md:flex-nowrap md:overflow-hidden shadow-xl bg-white flex flex-wrap items-center justify-between relative md:w-64 z-10 py-4 px-6"
>
  <div
    class="md:flex-col md:items-stretch md:min-h-full md:flex-nowrap px-0 flex flex-wrap items-center justify-between w-full mx-auto"
  >
    <!-- Toggler -->
    <button
      class="cursor-pointer text-black opacity-50 md:hidden px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent"
      type="button"
      on:click={() => toggleCollapseShow('bg-white m-2 py-3 px-6')}
    >
      <i class="fas fa-bars"></i>
    </button>
    <!-- Brand -->
    <a
      use:link
      class="md:block text-left md:pb-2 text-blueGray-600 mr-0 inline-block whitespace-nowrap text-sm uppercase font-bold p-4 px-0"
      href="/"
    >
      {$t('app.name')}
    </a>
      {#if $isMobileStore}
      <!-- User -->
      <ul class="md:hidden items-center flex flex-wrap list-none">
        <li class="inline-block relative">
          <NotificationDropdown />
        </li>
        <li class="inline-block relative">
          <UserDropdown />
        </li>
      </ul>
    {/if}
    <!-- Collapse -->
    <div
      class="md:flex md:flex-col md:items-stretch md:opacity-100 md:relative md:mt-4 md:shadow-none shadow absolute top-0 left-0 right-0 z-40 overflow-y-auto overflow-x-hidden h-auto items-center flex-1 rounded {collapseShow}"
    >
      <!-- Collapse header -->
      <div
        class="md:min-w-full md:hidden block pb-4 mb-4 border-b border-solid border-blueGray-200"
      >
        <div class="flex flex-wrap">
          <div class="w-6/12">
            <a
              use:link
              class="md:block text-left md:pb-2 text-blueGray-600 mr-0 inline-block whitespace-nowrap text-sm uppercase font-bold p-4 px-0"
              href="/"
            >
              {$t('app.name')} &copy;
            </a>
          </div>
          <div class="w-6/12 flex justify-end">
            <button
              type="button"
              class="cursor-pointer text-black opacity-50 md:hidden px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent"
              on:click={() => toggleCollapseShow('hidden')}
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
      <!-- Form -->
      <form>
        <div class="mb-3 pt-0">
          <input
            type="text"
            placeholder="{$t('search.placeholder')}"
            class="border-0 px-3 py-2 h-8 border border-solid border-blueGray-500 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-base leading-snug shadow-none outline-none focus:outline-none w-full font-normal"
            bind:value={searchTerm}
          />
        </div>
      </form>

      <a
      use:link
      href="/admin/dashboard"
      class="text-xs uppercase py-3 font-bold block {location && location.href && location.href.indexOf('/admin/dashboard') !== -1 ? 'text-black-500 hover:text-blue-600':'text-blueGray-700 hover:text-blueGray-500'}"
    >
      <i
        class="fas fa-tv mr-2 text-sm {location && location.href && location.href.indexOf('/admin/dashboard') !== -1 ? 'opacity-75' : 'text-blueGray-300'}"
      ></i>
      {$t('nav.dashboard')}
    </a>

      {#each filteredItems as record}

        <!-- Divider -->
        <hr class="my-4 md:min-w-full" />
        <!-- Heading -->
        <h6
          class="md:min-w-full text-blueGray-500 text-xs uppercase font-bold block pt-1 pb-4 no-underline"
        >
          {getDescripText(record.descrip)}
        </h6>
        <!-- Navigation -->

        <ul class="md:flex-col md:min-w-full flex flex-col list-none">
          {#if record.children && record.children.length > 0}
            {#each record.children as child}
              <li class="items-center">
                <a
                  use:link
                  href="{`${child.uri}/${child.id}`}"
                  class="text-xs uppercase py-3 font-bold block {location.href.indexOf('/admin/dashboard') !== -1 ? 'text-black-500 hover:text-blue-600':'text-blueGray-700 hover:text-blueGray-500'}"
                >
                  <i
                    class="fas {child.modulo_icon} mr-2 text-sm {location.href.indexOf('/admin/dashboard') !== -1 ? 'opacity-75' : 'text-blueGray-300'}"
                  ></i>
                  {getDescripText(child.descrip)}&nbsp;&nbsp;
                  {#if child.children && child.children.length > 0}
                    <!-- Botón para cambiar la visibilidad de los nietos -->
                    <button on:click={() => toggleChild(child.id)} class="text-sm text-gray-500 hover:text-gray-700">
                      {#if expandedChild === child.id}
                        <i class="fas fa-minus"></i> <!-- Icono de menos -->
                      {:else}
                        <i class="fas fa-plus"></i> <!-- Icono de más -->
                      {/if}
                    </button>
                  {/if}
                
                </a>
        


                {#if expandedChild === child.id && child.children && child.children.length > 0}
                  <!-- Custom class for grandchildren and whitespace -->
                  <ul class="space-y-2" style="margin-left: 15px;" transition:fade={{ duration: 300 }}>
                    {#each child.children as grandchild}
                      <li class="items-center">
                        <a
                          use:link
                          href="{grandchild.uri}"
                          class="text-xs uppercase py-3 font-bold block {location.href.indexOf('/admin/dashboard') !== -1 ? 'text-blue-500 hover:text-blue-600':'text-blueGray-700 hover:text-blueGray-500'}"
                        >
                          <i
                            class="fas {grandchild.modulo_icon} mr-2 text-sm {location.href.indexOf('/admin/dashboard') !== -1 ? 'opacity-75' : 'text-blueGray-300'}"
                          ></i>
                          {getDescripText(grandchild.descrip)}
                        </a>
                      </li>
                    {/each}
                  </ul>
                {/if}
              </li>
            {/each}
          {/if}
        </ul>          
      {/each}
  </div>
</nav>
